import { Controller } from 'stimulus'

// Owns the client-side lifecycle of the shared pronunciation dictionary drawer.
// Data is deliberately kept in the form: it is discarded before a frame navigation
// so an editor never accidentally saves changes from a different result page.
export default class extends Controller {
  static targets = [
    'trigger', 'backdrop', 'panel', 'closeButton', 'frame', 'form', 'searchForm', 'search',
    'rows', 'row', 'rowTemplate', 'dirtyNotice'
  ];

  static values = {
    entriesUrl: String,
    frameId: String,
    provider: String,
    previewUrl: String
  };

  connect () {
    this.dirty = false
    this.opened = false
    this.newRowIndex = 0
  }

  disconnect () {
    this.clearSearchTimer()
    this.cleanupPreviewAudio()
    document.body.classList.remove('article-audio-manager--dictionary-open')
  }

  open (event) {
    event?.preventDefault()
    this.lastFocusedElement = event?.currentTarget || document.activeElement
    this.opened = true
    this.dirty = false
    this.updateDirtyNotice()
    this.triggerTarget.setAttribute('aria-expanded', 'true')
    this.triggerTarget.hidden = true
    this.backdropTarget.setAttribute('aria-hidden', 'false')
    this.element.classList.add('is-open')
    document.body.classList.add('article-audio-manager--dictionary-open')
    this.refreshFrame()
    window.setTimeout(() => this.closeButtonTarget.focus(), 0)
  }

  close (event) {
    event?.preventDefault()
    if (!this.confirmDiscardChanges()) return

    this.finishClose()
  }

  backdropClose (event) {
    if (event.target !== this.backdropTarget) return
    this.close(event)
  }

  keydown (event) {
    if (!this.opened) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.close(event)
      return
    }

    if (event.key === 'Tab') this.trapFocus(event)
  }

  markDirty () {
    if (!this.opened) return

    this.dirty = true
    this.updateDirtyNotice()
  }

  updateDirtyNotice () {
    if (!this.hasDirtyNoticeTarget) return

    this.dirtyNoticeTarget.hidden = !this.dirty
  }

  scheduleSearch () {
    this.clearSearchTimer()
    this.searchTimer = window.setTimeout(() => this.submitSearchForm(), 500)
  }

  searchKeydown (event) {
    if (event.key !== 'Enter') return

    event.preventDefault()
    this.clearSearchTimer()
    this.submitSearchForm()
  }

  submitSearch (event) {
    if (event.currentTarget?.dataset.dictionaryDrawerBypass === 'true') {
      delete event.currentTarget.dataset.dictionaryDrawerBypass
      return
    }

    event.preventDefault()
    this.submitSearchForm()
  }

  navigate (event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return

    if (!this.confirmDiscardChanges()) {
      event.preventDefault()
      return
    }

    const url = event.currentTarget?.href
    if (!url || !this.hasFrameTarget) return

    event.preventDefault()
    this.dirty = false
    this.updateDirtyNotice()
    this.frameTarget.setAttribute('src', url)
  }

  addRow () {
    if (!this.hasRowsTarget || !this.hasRowTemplateTarget) return

    const markup = this.rowTemplateTarget.innerHTML.replace(/__NEW_INDEX__/g, `new_${this.newRowIndex++}`)
    this.rowsTarget.insertAdjacentHTML('afterbegin', markup)
    this.markDirty()
    this.rowsTarget.querySelector("input[type='text']")?.focus()
  }

  captureRowSnapshots () {
    if (!this.hasRowsTarget) return

    this.rowTargets.forEach(row => {
      row.dataset.dictionaryDrawerInitialState = JSON.stringify(this.rowValues(row))
    })
  }

  filterSubmittedEntries (event) {
    const body = event.detail?.fetchOptions?.body
    if (!this.isDictionarySaveRequest(event, body)) return

    this.rowTargets.forEach(row => {
      if (this.rowNeedsSave(row)) return

      row.querySelectorAll('input[name], select[name], textarea[name]').forEach(input => {
        body.delete(input.name)
      })
    })

    this.rowTargets.forEach(row => this.filterUnchangedFields(row, body))
  }

  toggleDelete (event) {
    const row = event.currentTarget.closest("[data-dictionary-drawer-target~='row']")
    if (!row) return

    const isDeleted = row.classList.toggle('is-marked-for-deletion')
    const destroyInput = row.querySelector("[data-dictionary-drawer-target~='destroyInput']")
    const deleteButton = row.querySelector("[data-dictionary-drawer-target~='deleteButton']")
    const undoButton = row.querySelector("[data-dictionary-drawer-target~='undoButton']")

    if (destroyInput) destroyInput.value = isDeleted ? '1' : '0'
    const controls = row.querySelectorAll(
      "[data-dictionary-drawer-target~='editable'], [data-dictionary-drawer-target~='previewButton']"
    )
    controls.forEach(control => {
      control.disabled = isDeleted
    })
    if (deleteButton) deleteButton.hidden = isDeleted
    if (undoButton) undoButton.hidden = !isDeleted
    this.markDirty()
  }

  updatePreviewAvailability (event) {
    const input = event.currentTarget
    const cell = input.closest('.article-audio-manager__dictionary-pronunciation-cell')
    const button = cell?.querySelector("[data-dictionary-drawer-target~='previewButton']")
    if (button) button.disabled = input.value.trim().length === 0
  }

  updatePronunciationRequirement (event) {
    const row = event.currentTarget.closest("[data-dictionary-drawer-target~='row']")
    if (!row) return

    const keywordInput = row.querySelector("input[name$='[keyword]']")
    const pronunciationInputs = Array.from(
      row.querySelectorAll("[data-dictionary-drawer-target~='pronunciationInput']")
    )
    const requiresPronunciation = keywordInput?.value.trim().length > 0 &&
      pronunciationInputs.every(input => input.value.trim().length === 0)

    pronunciationInputs.forEach(input => {
      input.required = false
      input.setCustomValidity('')
    })

    const [firstPronunciationInput] = pronunciationInputs
    if (!firstPronunciationInput || !requiresPronunciation) return

    firstPronunciationInput.required = true
    firstPronunciationInput.setCustomValidity('Enter a pronunciation for at least one language.')
  }

  async preview (event) {
    event.preventDefault()

    const button = event.currentTarget
    const cell = button.closest('.article-audio-manager__dictionary-pronunciation-cell')
    const input = cell?.querySelector("[data-dictionary-drawer-target~='pronunciationInput']")
    const error = cell?.querySelector("[data-dictionary-drawer-target~='previewError']")
    const language = button.dataset.dictionaryDrawerLanguage
    const previewUrl = this.previewUrlValue

    if (!input || !language || !previewUrl || input.value.trim().length === 0) return

    this.clearPreviewError(error)
    button.disabled = true

    try {
      const formData = new FormData()
      formData.append('article_audio_pronunciation_entry[pronunciation]', input.value)
      formData.append('language', language)
      formData.append('selection[provider]', this.providerValue)

      const response = await fetch(previewUrl, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': this.csrfToken() }
      })

      if (!response.ok) throw new Error((await response.text()) || 'Preview failed')

      this.playPreviewAudio(await response.blob())
    } catch (errorResponse) {
      this.showPreviewError(error, errorResponse.message || 'Preview failed')
    } finally {
      button.disabled = input.value.trim().length === 0
    }
  }

  saveFinished (event) {
    if (!event.detail?.success) return

    this.dirty = false
    this.updateDirtyNotice()
  }

  refreshFrame () {
    if (!this.hasFrameTarget || !this.entriesUrlValue) return

    const url = new URL(this.entriesUrlValue, window.location.origin)
    url.searchParams.set('_dictionary_opened_at', Date.now().toString())
    url.searchParams.set('dictionary_frame_id', this.frameIdValue)
    this.frameTarget.removeAttribute('src')
    this.frameTarget.setAttribute('src', url.toString())
  }

  submitSearchForm () {
    if (!this.hasSearchFormTarget || !this.confirmDiscardChanges()) return

    this.dirty = false
    this.updateDirtyNotice()
    this.restoreSearchFocusAfterRender = true
    this.searchFormTarget.dataset.dictionaryDrawerBypass = 'true'
    this.searchFormTarget.requestSubmit()
  }

  restoreSearchFocus () {
    if (!this.restoreSearchFocusAfterRender || !this.hasSearchTarget) return

    this.restoreSearchFocusAfterRender = false
    const search = this.searchTarget
    window.requestAnimationFrame(() => {
      if (!search.isConnected) return

      search.focus()
      search.setSelectionRange(search.value.length, search.value.length)
    })
  }

  rowNeedsSave (row) {
    const id = row.querySelector("input[name$='[id]']")
    if (!id || row.dataset.dictionaryDrawerInvalidValue === 'true') return true

    return row.dataset.dictionaryDrawerInitialState !== JSON.stringify(this.rowValues(row))
  }

  isDictionarySaveRequest (event, body) {
    if (!(body instanceof URLSearchParams) && !(body instanceof FormData)) return false
    if (body.get('_method') !== 'patch' || !this.hasFormTarget) return false

    const requestUrl = new URL(event.detail.url, window.location.origin)
    const formUrl = new URL(this.formTarget.action, window.location.origin)
    return requestUrl.pathname === formUrl.pathname
  }

  rowState (row) {
    return JSON.stringify(this.rowValues(row))
  }

  rowValues (row) {
    return Array.from(row.querySelectorAll('input, select, textarea'))
      .reduce((values, input) => {
        values[input.name] = input.value
        return values
      }, {})
  }

  filterUnchangedFields (row, body) {
    const id = row.querySelector("input[name$='[id]']")
    const initialState = row.dataset.dictionaryDrawerInitialState
    if (!id || !initialState) return

    const initialValues = JSON.parse(initialState)
    row.querySelectorAll("input[name$='[keyword]'], [data-dictionary-drawer-target~='pronunciationInput']").forEach(input => {
      if (initialValues[input.name] === input.value) body.delete(input.name)
    })
  }

  confirmDiscardChanges () {
    return !this.dirty || window.confirm('Discard unsaved dictionary changes?')
  }

  finishClose () {
    this.clearSearchTimer()
    this.cleanupPreviewAudio()
    this.opened = false
    this.dirty = false
    this.element.classList.remove('is-open')
    this.backdropTarget.setAttribute('aria-hidden', 'true')
    this.triggerTarget.hidden = false
    this.triggerTarget.setAttribute('aria-expanded', 'false')
    document.body.classList.remove('article-audio-manager--dictionary-open')
    window.setTimeout(() => this.lastFocusedElement?.focus?.(), 0)
  }

  trapFocus (event) {
    const selector = [
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])', "[tabindex]:not([tabindex='-1'])"
    ].join(', ')
    const focusable = Array.from(this.panelTarget.querySelectorAll(selector))
      .filter(element => !element.hidden && element.offsetParent !== null)
    if (focusable.length === 0) return

    const [first] = focusable
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  playPreviewAudio (blob) {
    this.cleanupPreviewAudio()
    this.previewAudioUrl = window.URL.createObjectURL(blob)
    this.previewAudio = new Audio(this.previewAudioUrl)
    this.previewAudio.addEventListener('ended', () => this.cleanupPreviewAudio(), { once: true })
    this.previewAudio.play()
  }

  cleanupPreviewAudio () {
    this.previewAudio?.pause()
    this.previewAudio = null
    if (this.previewAudioUrl) window.URL.revokeObjectURL(this.previewAudioUrl)
    this.previewAudioUrl = null
  }

  showPreviewError (element, message) {
    if (!element) return

    element.textContent = message
    element.hidden = false
  }

  clearPreviewError (element) {
    if (!element) return

    element.textContent = ''
    element.hidden = true
  }

  clearSearchTimer () {
    if (!this.searchTimer) return

    window.clearTimeout(this.searchTimer)
    this.searchTimer = null
  }

  csrfToken () {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute('content') || ''
  }
}
